import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { AiFillCheckCircle, AiFillExclamationCircle } from "react-icons/ai";
import { FaThumbsUp } from "react-icons/fa";

import { Card, IActionButton } from "../../components/Card";
import Modal from "../../components/Modal";
import {
    BackImgContainer,
    TransparentContainer,
} from "../../components/Containers";

import breakfast from "../../assets/breakfast.svg";
import lunch from "../../assets/lunch.svg";
import snack from "../../assets/afternoon_snack.svg";
import dinner from "../../assets/dinner.svg";
import generic from "../../assets/generic_food.svg";

import SelectionMenu from "./SelectionMenu";
import { Customer, Menu, Order } from "../../models";
import { ComponentStatus } from "../../components";
import { CustomerService, SessionService } from "../../services";

import { useSelector, useDispatch } from "react-redux";
import { IState } from "../../app-state/reducer";
import { Actions } from "../../app-state/actions";
import { FavaloroSpinner } from "../../components/Spinner";
import { ApplicationType, ThisPaths } from "../../shared/Constants";
import { useHistory } from "react-router-dom";
import { errorAlert, infoAlert } from "../../components/Alerts";
import { JumpBtn } from "../../components/Buttons";
import { env } from "../../env";

const StyledContainer = styled.div`
    width: 65vw;
    height: 18vw;
    -webkit-flex-wrap: wrap;
    flex-wrap: wrap;
    display: flex;
    justify-content: space-around;
    padding-top: 3vw;

    @media (max-width: 1000px) {
        width: 100vw;
        overflow: scroll;
        height: 95%;
    }
`;

const resolveImg = (key: string): string => {
    switch (key.toLowerCase()) {
        case "almuerzo":
            return lunch;
        case "cena":
            return dinner;
        case "desayuno":
            return breakfast;
        case "merienda":
            return snack;
        default:
            return generic;
    }
};

const isTotem =
    env.REACT_APP_APPLICATION_TYPE === ApplicationType.TOTEM;

const DiningRoom: React.FC = () => {
    const history = useHistory();
    const [customer, setCustomer] = useState<Customer>();
    const [menu, setMenu] = useState<Menu>();
    const [status, setStatus] = useState<ComponentStatus>("complete");
    const [error, setError] = useState<string>("");

    const dispatch = useDispatch();
    const order = useSelector<IState, Order | undefined>(
        (state) => state.order
    );

    const [openModal, setOpenModal] = useState(false);

    const onTicketClick = (id: number) => {
        //mariel: clearTimeout(window.noActionTimer);
        const menu = customer?.menus.find((m) => m.menuID === id);
        setMenu(menu);
        setOpenModal(!openModal);
    };

    const buttons = (menu: Menu): IActionButton[] => [
        {
            label: (
                <>
                    <FaThumbsUp /> Reservar Pedido
                </>
            ),
            onClick: () => onTicketClick(menu.menuID),
        },
    ];

    useEffect(() => {
        if (window.innerWidth > 1000) {
            document.body.style.overflow = "hidden";
        }

        if (!order) {
            const fetchCustomer = async () => {
                setStatus("loading");
                const sr = await CustomerService.OrderOptions();

                if (sr.status()) {
                    setCustomer(sr.data);
                    dispatch(Actions.SET_CUSTOMER(sr.data));
                    setStatus("complete");
                } else {
                    setStatus("errored");
                    setError(sr.error);
                }
            };

            fetchCustomer();
        }
    }, [dispatch, order]);

    const modalBody = () => {
        if (order) {
            return (
                <TransparentContainer>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-around",
                            alignContent: "center",
                            alignItems: "center",
                            height: "100%",
                            textAlign: "center",
                        }}
                    >
                        {order.isError ? (
                            <>
                                <AiFillExclamationCircle
                                    style={{
                                        width: "auto",
                                        height: "58px",
                                        color: "red",
                                    }}
                                />
                                <span>{order.orderMessageAlert}</span>
                            </>
                        ) : (
                            <>
                                <AiFillCheckCircle
                                    style={{
                                        width: "auto",
                                        height: "58px",
                                        color: "green",
                                    }}
                                />
                                <span>
                                    Menú: {order?.menuDescription} -
                                    {order?.menuTypeDescription}
                                </span>
                                <span>{order.orderMessageTime}</span>
                                <span>{order.orderMessageAlert}</span>
                            </>
                        )}
                    </div>
                </TransparentContainer>
            );
        }

        return (
            <SelectionMenu
                allowDelivery={isTotem ? false : customer?.allowDelivery}
                customerId={customer?.customerID}
                menu={menu!}
            />
        );
    };

    const onCloseModal = () => {
        dispatch(Actions.REMOVE_ORDER());
        setOpenModal(!openModal);

        if (isTotem) {
            SessionService.LogOut();
            dispatch(Actions.RESET_STATE());
            history.replace(ThisPaths.LOGIN);
        }
    };

    const render = () => {
        switch (status) {
            case "loading":
                return <FavaloroSpinner />;
            case "complete": {
                if (customer?.menus.length === 0)
                    return infoAlert("Ya pediste todos las comidas del día!");
                return (
                    <>
                        <Modal
                            title={order ? "Nuevo Pedido" : "Carga tu Pedido"}
                            active={openModal}
                            hideModal={onCloseModal}
                            children={modalBody()}
                            footer={
                                <JumpBtn
                                    borderColor={"black"}
                                    colorOnHover={"rgba(11, 2, 2, 0.44)"}
                                    value="Cerrar"
                                    onClick={onCloseModal}
                                />
                            }
                        />
                        <StyledContainer>
                            {customer?.menus.map((menu, index) => (
                                <Card
                                    key={index}
                                    srcImg={resolveImg(menu.description)}
                                    title={menu.description}
                                    onClick={() => onTicketClick(menu.menuID)}
                                    buttons={buttons(menu)}
                                    description={`${menu.price} creditos`}
                                />
                            ))}
                        </StyledContainer>
                    </>
                );
            }
            case "errored":
                return errorAlert(error);
            default:
                return null;
        }
    };

    return <BackImgContainer>{render()}</BackImgContainer>;
};

export default DiningRoom;
